import React, {
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  AssetForm, BeneficiaryForm, ButtonBar, ErrorMessage, ScreenHeader
} from '../../components';
import {
  AssetContext, AssetProvider, BeneficiariesContext, BeneficiariesProvider
} from '../../context';
import { useCreate, useCreateOrUpdate } from '../../hooks';
import { Asset } from '../../types';

import { NewWillContext } from '.';

export function NewWillAddAssetScreen() {
  const [ searchParams ] = useSearchParams();
  const editId = searchParams.get('edit');

  return (
    <AssetProvider assetId={editId}>
      <BeneficiariesProvider assetId={editId}>
        <NewWillAddAssetScreenContent />
      </BeneficiariesProvider>
    </AssetProvider>
  );
}

export function NewWillAddAssetScreenContent() {
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const editId = searchParams.get('edit');

  const { asset } = useContext(AssetContext);
  const { beneficiaries } = useContext(BeneficiariesContext);

  const { will } = useContext(NewWillContext);

  const [ error, setError ] = useState('');
  const [ assetType, setAssetType ] = useState(null);
  const [ selectedBeneficiaries, setSelectedBeneficiaries ] = useState(beneficiaries || []);

  const { handleCreateOrUpdate: handleCreateOrUpdateAsset } = useCreateOrUpdate('assets');
  const { handleCreate: handleCreateBequest } = useCreate('bequests');

  useEffect(() => {
    if (!asset) 
      return;

    console.log(' new will add asset screen content use effect', asset, beneficiaries);
    (document.getElementById('asset-name') as HTMLInputElement).value = asset.name;
    (document.getElementById('description') as HTMLInputElement).value = asset.description;
    setAssetType({ id: asset.type });
    setSelectedBeneficiaries(beneficiaries || []);
  }, [ beneficiaries, asset?.description, asset?.name, asset?.type, asset ]);

  const handleBack = useCallback(() => {
    navigate('../asset-summary');
  }, [ navigate ]);

  const handleNext = useCallback(async () => {
    const asset = {} as Asset;

    if (!assetType) {
      setError('Please select an asset type.');
      return;
    }
  
    asset.name = (document.getElementById('asset-name') as HTMLInputElement).value;
    asset.description = (document.getElementById('description') as HTMLInputElement).value;
    asset.type = assetType.id;

    if (!asset.name) {
      setError('Please fill in asset name.');
      return;
    }
    console.log('Handle NeztBeneficiaries', beneficiaries);
    if (!selectedBeneficiaries || selectedBeneficiaries.length === 0) {
      setError('Please select at least one beneficiary.');
      return;
    }

    const createdAsset = await handleCreateOrUpdateAsset(editId, {
      name: asset.name,
      description: asset.description,
      asset_type: assetType.id,
      type: asset.type,
      will_id: will.id
    });

    await Promise.all(selectedBeneficiaries.map((beneficiary) => handleCreateBequest({
      asset_id: createdAsset.id,
      beneficiary_id: beneficiary.id,
      proportion: beneficiary.percentage || 100
    })));    
    
    navigate('../asset-summary');
  }, [ assetType, beneficiaries, selectedBeneficiaries, handleCreateOrUpdateAsset, editId, will.id, navigate, handleCreateBequest ]);

  const handleChange = useCallback(option => {
    console.log('Handle Change', option);
    setAssetType(option);
  }, []);

  const handleSelectBeneficiaries = useCallback((selected) => {
    console.log('Handle Select Beneficiaries', selected);
    setSelectedBeneficiaries(selected);
  }, []);

  const handleRemoveBeneficiary = useCallback((person) => {
    setSelectedBeneficiaries((prev) => prev.filter((b) => b.id !== person.id));
  }, []);

  return (
    <div className="new-will-add-asset-screen screen">
      <ScreenHeader 
        title="Add Asset"
        description="Fill out the asset info"
        handleBack={handleBack} 
        steps={6} 
        step={2} 
      />
      <AssetForm 
        handleChange={handleChange} 
        value={assetType} 
      />
      <BeneficiaryForm 
        beneficiaries={selectedBeneficiaries}
        handleSelectBeneficiaries={handleSelectBeneficiaries} 
        handleRemoveBeneficiary={handleRemoveBeneficiary}
      />
      <ErrorMessage message={error} />
      <ButtonBar
        handlePrimary={handleNext}
        primaryText="Add Asset"
      />
    </div>
  );
}